import React, { useEffect, useState } from "react";
/* eslint-disable react/no-unknown-property -- project-standard Cypress selectors */
import PropTypes from "prop-types";
import {
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  CircularProgress
} from "@mui/material";
import styled from "styled-components";
import {
  AppointmentScheduleTableRow,
  AppointmentScheduleCardRow
} from "./appointment_schedule_row";
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { useIsMobile } from "../../../hooks/useIsMobile";

const emptyScheduleData = {
  start_date: "",
  end_date: "",
  recurring: "",
  description: ""
};

const checkScheduleData = (scheduleData) => {
  if (!scheduleData.start_date) return "Must select start date";
  if (!scheduleData.recurring) return "Must select a recrruing value";
  if (scheduleData.end_date) {
    if (new Date(scheduleData.end_date) <= new Date(scheduleData.start_date)) {
      return "End date must be after start date";
    }
  }
  return undefined; // Means data is valid
};

const generateHeadersForRequest = () => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
  return {
    "X-CSRF-Token": csrfToken,
    "Content-Type": "application/json",
    Accept: "application/json"
  };
};

// TODO remove old time off stuff (anything that has an end date in the past)
const AppointmentScheduleModal = ({ open, onClose }) => {
  const [scheduleObjects, setScheduleObjects] = useState([]);
  const [loading, setIsLoading] = useState(true);
  const [addNewData, setAddNewData] = useState(emptyScheduleData);
  const [hasThereBeenChanges, setHasThereBeenChanges] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    // TODO get schedule objects from API
    getScheduleObjects();
  }, []);

  const getScheduleObjects = async () => {
    try {
      setIsLoading(true);
      const response = await axios({
        method: "GET",
        url: "/peer_schedules.json",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        }
      });

      if (response.status === 200) {
        setScheduleObjects(response.data);
      }
    } catch {
      toastr.error("Failed to load peer schedules.");
    } finally {
      setIsLoading(false);
    }
  };

  const onEditNewData = (valueToUpdate, newValue) => {
    setAddNewData({ ...addNewData, [valueToUpdate]: newValue });
  };

  const onAddNewSchedule = async () => {
    try {
      setIsLoading(true);
      const error = checkScheduleData(addNewData);
      if (error) throw error;
      const response = await axios({
        method: "POST",
        url: "/peer_schedules.json",
        headers: generateHeadersForRequest(),
        data: {
          peer_schedule: { ...addNewData, all_day: true }
        }
      });

      if (response.status === 201) {
        setHasThereBeenChanges(true);
        // Reset form after successful creation
        setAddNewData(emptyScheduleData);
        getScheduleObjects();
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
      throw "Failed to add new schedule";
    } catch {
      setIsLoading(false);
      toastr.error("Failed to add new schedule");
      // Handle error (show toastr or error message)
    }
  };

  const onEditRow = async (valueToEdit, newValue, scheduleObjectId) => {
    try {
      let scheduleToUpdate = scheduleObjects.find(
        (scheduleObject) => scheduleObject.id === scheduleObjectId
      );
      if (!scheduleToUpdate) throw "Error";
      scheduleToUpdate[valueToEdit] = newValue;
      const error = checkScheduleData(scheduleToUpdate);
      if (error) throw error;
      const response = await axios({
        method: "PUT",
        url: `/peer_schedules/${scheduleToUpdate.id}.json`,
        headers: generateHeadersForRequest(),
        data: {
          peer_schedule: { ...scheduleToUpdate }
        }
      });
      if (response.status === 201 || response.status === 200) {
        setHasThereBeenChanges(true);
        setScheduleObjects(
          scheduleObjects.map((scheduleObject) => {
            if (scheduleToUpdate.id === scheduleObject.id) return scheduleToUpdate;
            return scheduleObject;
          })
        );
        return;
      }
      throw "Failed to edit schedule";
    } catch {
      toastr.error("Failed to edit schedule");
    }
  };

  const onDeleteRow = async (scheduleObjectId) => {
    try {
      let scheduleToDelete = scheduleObjects.find(
        (scheduleObject) => scheduleObject.id === scheduleObjectId
      );
      if (!scheduleToDelete) throw "Error";
      const response = await axios({
        method: "DELETE",
        url: `/peer_schedules/${scheduleToDelete.id}.json`,
        headers: generateHeadersForRequest()
      });
      if (response.status === 204 || response.status === 200) {
        setHasThereBeenChanges(true);
        setScheduleObjects(
          scheduleObjects.filter((scheduleObject) => {
            if (scheduleToDelete.id === scheduleObject.id) return false;
            return true;
          })
        );
        return;
      }
      throw "Failed to delete schedule";
    } catch {
      toastr.error("Failed to delete schedule");
    }
  };

  const close = () => {
    if (hasThereBeenChanges) {
      window.location.reload();
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={close} aria-labelledby="peer-schedule-modal-title">
      <StyledBox className="ccw-dialog" role="dialog" aria-modal="true" aria-busy={loading}>
        {loading && (
          <LoadingContainer sx={{ margin: "auto" }}>
            <CircularProgress />
          </LoadingContainer>
        )}
        <Typography id="peer-schedule-modal-title" variant="h4" component="h2">
          Time off selector
        </Typography>
        <Typography variant="h6" component="h3">
          Add new time:
        </Typography>
        {isMobile ? (
          <AppointmentScheduleCardRow isNew row={addNewData} onEdit={onEditNewData} />
        ) : (
          <AppointmentScheduleTableRow isNew row={addNewData} onEdit={onEditNewData} />
        )}
        <ActionsRow>
          <Button
            explorator_test_id="peer-schedule-add"
            className="ccw-button ccw-button--primary"
            variant="contained"
            onClick={onAddNewSchedule}
            size={isMobile ? "small" : "medium"}
          >
            Add New
          </Button>
        </ActionsRow>
        <Typography variant="h6" component="h3">
          Edit or delete existing time off
        </Typography>
        {isMobile ? (
          <MobileScheduleList>
            {scheduleObjects.length === 0 ? (
              <EmptyState>No schedules yet.</EmptyState>
            ) : (
              scheduleObjects.map((row) => (
                <AppointmentScheduleCardRow
                  row={row}
                  key={row.id}
                  onEdit={onEditRow}
                  onDeleteRow={onDeleteRow}
                />
              ))
            )}
          </MobileScheduleList>
        ) : (
          <TableContainer className="ccw-table-shell" component="div" sx={{ overflowX: "auto" }}>
            <Table className="ccw-table" sx={{ minWidth: 650 }} aria-label="peer schedule table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">Recurring</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell align="right">End Date</TableCell>
                  <TableCell align="right">Description</TableCell>
                  <TableCell align="right">Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scheduleObjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No schedules yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  scheduleObjects.map((row) => (
                    <AppointmentScheduleTableRow
                      row={row}
                      key={row.id}
                      onEdit={onEditRow}
                      onDeleteRow={onDeleteRow}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <ActionsRow>
          <Button
            explorator_test_id="peer-schedule-close"
            className="ccw-button"
            variant="outlined"
            onClick={close}
          >
            Close
          </Button>
        </ActionsRow>
      </StyledBox>
    </Modal>
  );
};

AppointmentScheduleModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func
};

const StyledBox = styled(Box)`
  && {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(900px, 90vw);
    max-height: 90vh;
    overflow-y: auto;
    background-color: #ffffff;
    border: 1px solid var(--ccw-color-border-strong);
    border-radius: var(--ccw-surface-radius);
    box-shadow: none;
    padding: 2rem;
  }

  @media (max-width: 768px) {
    && {
      padding: 1.25rem;
      width: 95vw;
      max-height: 95vh;
    }
  }
`;

const LoadingContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 999999;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem 0;
  gap: 0.75rem;
`;

const MobileScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const EmptyState = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: #475569;
  border: 1px dashed #cbd5f5;
  border-radius: 0.75rem;
`;

const ScheduleButton = styled(Button)`
  && {
    display: block;
    width: 100%;
  }
`;

const AppointmentScheduleModalButton = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const onClose = () => {
    setModalOpen(false);
  };

  return (
    <>
      <ScheduleButton
        explorator_test_id="peer-schedule-open"
        className="ccw-button ccw-button--primary"
        variant="contained"
        onClick={() => setModalOpen(true)}
      >
        Manage Time Off
      </ScheduleButton>
      <AppointmentScheduleModal open={modalOpen} onClose={onClose} />
    </>
  );
};

export default AppointmentScheduleModalButton;
